// src/components/ExistingEvents.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Modal from './Modal';
import { format } from 'date-fns';
import { FaEdit, FaTrashAlt, FaEye, FaCalendarAlt, FaLink, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';

/**
 * ExistingEvents Component
 * 
 * Displays and manages all existing events in a professional data table
 * with sorting, filtering, and action buttons
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onEditEvent - Function to handle editing an event
 * @returns {JSX.Element} - Rendered component
 */
const ExistingEvents = ({ onEditEvent }) => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const [modal, setModal] = useState({ 
    isVisible: false, 
    title: "", 
    message: "", 
    onConfirm: null,
    type: "info" 
  });

  /**
   * Fetch all events from Firestore
   */
  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const eventsCollection = collection(db, "events");
      const eventSnapshot = await getDocs(eventsCollection);
      const eventList = eventSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEvents(eventList);
      setFilteredEvents(eventList);
    } catch (error) {
      console.error("Error fetching events:", error);
      setModal({
        isVisible: true,
        title: "Error",
        message: "Failed to load events. Please try again.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initialize component by fetching events
   */
  useEffect(() => {
    fetchEvents();
  }, []);



  /**
   * Sort and filter events based on search term and sort configuration
   */
  useEffect(() => {
    let processedEvents = [...events];
    
    // Apply search filter first
    if (searchTerm.trim() !== '') {
      const lowercasedTerm = searchTerm.toLowerCase();
      processedEvents = processedEvents.filter(event => 
        event.title?.toLowerCase().includes(lowercasedTerm) || 
        event.venue?.toLowerCase().includes(lowercasedTerm) ||
        event.id?.toLowerCase().includes(lowercasedTerm)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      processedEvents.sort((a, b) => {
        // Handle dates specially
        if (sortConfig.key === 'eventDate' || sortConfig.key === 'lastDateToRegister') {
          const aValue = a[sortConfig.key] instanceof Timestamp ? a[sortConfig.key].toDate() : new Date(0);
          const bValue = b[sortConfig.key] instanceof Timestamp ? b[sortConfig.key].toDate() : new Date(0);
          
          if (sortConfig.direction === 'asc') {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        }
        
        // Handle regular string/number values
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredEvents(processedEvents);
  }, [events, searchTerm, sortConfig]);

  /**
   * Request confirmation before deleting an event
   * @param {Object} event - The event to delete
   */
  const handleDeleteRequest = (event) => {
    setModal({
      isVisible: true,
      title: "Confirm Deletion",
      message: `Are you sure you want to delete the event "${event.title}"? This action cannot be undone.`,
      type: "warning",
      onConfirm: () => handleConfirmDelete(event.id)
    });
  };

  /**
   * Delete an event after confirmation
   * @param {string} id - The ID of the event to delete
   */
  const handleConfirmDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "events", id));
      setModal({ 
        isVisible: true, 
        title: "Success", 
        message: "Event deleted successfully.", 
        type: "success" 
      });
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      setModal({
        isVisible: true,
        title: "Error",
        message: "Failed to delete event.",
        type: "error"
      });
    }
  };

  /**
   * Handle editing an event
   * @param {string} eventId - The ID of the event to edit
   */
  const handleEdit = (eventId) => {
    onEditEvent(eventId); // Trigger the edit event handler passed from AdminDashboard
  };

  /**
   * Handle sorting when a column header is clicked
   * @param {string} key - The key to sort by
   */
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Render sort indicator for table headers
   * @param {string} columnKey - The column key to check
   * @returns {JSX.Element|null} - Sort indicator icon
   */
  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="ml-1 text-gray-400 inline" size={12} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1 text-indigo-600 inline" size={12} /> 
      : <FaSortDown className="ml-1 text-indigo-600 inline" size={12} />;
  };

  /**
   * Format date values consistently
   * @param {Timestamp|Date} date - The date to format
   * @returns {string} - Formatted date string
   */
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date instanceof Timestamp) {
        return format(date.toDate(), 'MMM d, yyyy');
      }
      return format(new Date(date), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  /**
   * Get status of an event based on dates
   * @param {Object} event - The event to check
   * @returns {Object} - Status information
   */
  const getEventStatus = (event) => {
    const now = new Date();
    
    if (!event.eventDate) {
      return { label: 'No Date', color: 'gray' };
    }
    
    const eventDate = event.eventDate instanceof Timestamp 
      ? event.eventDate.toDate() 
      : new Date(event.eventDate);
      
    const lastRegDate = event.lastDateToRegister instanceof Timestamp 
      ? event.lastDateToRegister.toDate() 
      : new Date(event.lastDateToRegister);
    
    if (eventDate < now) {
      return { label: 'Completed', color: 'gray' };
    } else if (lastRegDate < now) {
      return { label: 'Registration Closed', color: 'yellow' };
    } else {
      return { label: 'Active', color: 'green' };
    }
  };

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Search and Actions Bar */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-grow max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <button
            onClick={fetchEvents}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white overflow-hidden shadow-md rounded-lg">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <FaCalendarAlt className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No events found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search term.' : 'Get started by creating a new event.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('title')}
                  >
                    <span className="flex items-center">
                      Title {renderSortIndicator('title')}
                    </span>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('lastDateToRegister')}
                  >
                    <span className="flex items-center">
                      Last Registration {renderSortIndicator('lastDateToRegister')}
                    </span>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('eventDate')}
                  >
                    <span className="flex items-center">
                      Event Date {renderSortIndicator('eventDate')}
                    </span>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvents.map((event) => {
                  const status = getEventStatus(event);
                  return (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            {event.bannerImageUrl ? (
                              <img 
                                src={event.bannerImageUrl} 
                                alt={event.title} 
                                className="h-10 w-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/40?text=YxC";
                                }}
                              />
                            ) : (
                              <FaCalendarAlt className="text-indigo-500" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{event.title}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <FaLink className="mr-1" size={12} />
                              <span className="truncate max-w-xs">{event.id}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(event.lastDateToRegister)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(event.eventDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${status.color === 'green' ? 'bg-green-100 text-green-800' : 
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-gray-100 text-gray-800'}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(event.id)}
                            className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 p-2 rounded-full transition-colors"
                            title="Edit Event"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(event)}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 p-2 rounded-full transition-colors"
                            title="Delete Event"
                          >
                            <FaTrashAlt size={16} />
                          </button>
                          <a
                            href={`/event/${event.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 p-2 rounded-full transition-colors"
                            title="View Event"
                          >
                            <FaEye size={16} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Event Count */}
      <div className="mt-4 text-sm text-gray-500">
        {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
        {searchTerm && filteredEvents.length !== events.length && ` (filtered from ${events.length})`}
      </div>

      {/* Modal */}
      <Modal
        isVisible={modal.isVisible}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ isVisible: false, title: "", message: "", onConfirm: null, type: "info" })}
        onConfirm={modal.onConfirm}
        type={modal.type}
      />
    </motion.div>
  );
};

export default ExistingEvents;